import { useEffect, useMemo, useRef, useState } from 'react';
import { useCanSeeWarehouses, usePermission } from '../../store/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Modal, Field, Select, InputNumber, Input, Segmented, Button, Tag, CodeChip, Alert, message, cn,
} from '../../ui';
import { Plus, Trash2 } from 'lucide-react';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { formatIstDate } from '../../lib/time';
import { warehouseOption } from '../../lib/warehouseLabel';
import { fmtQty } from '../../lib/format';

/**
 * A colour per reason, shared by this form's dropdown, the register's Reason cell and the reason-mix
 * strip above the rows. ONE definition, so a reason cannot be gold in the picker and blue in the
 * list it lands in. Tailwind classes rather than inline hexes: the theme's colour variables are
 * tree-shaken out of the JS bundle.
 */
export const REASON_DOT: Record<string, string> = {
  PHYSICAL_COUNT: 'bg-sky-500',
  DAMAGE: 'bg-danger-500',
  EXPIRY: 'bg-warning-500',
  THEFT: 'bg-violet-500',
  SAMPLE: 'bg-harvest-400',
  PRODUCTION_LOSS: 'bg-lime-500',
  OTHER: 'bg-subtle/55',
};
/** Anything outside the enum (older rows, an import) still gets a stable colour of its own. */
const SPARE_DOTS = ['bg-brand-500', 'bg-sky-600', 'bg-harvest-500', 'bg-violet-400', 'bg-lime-600', 'bg-warning-600'];
export const dotFor = (reason: string, index = 0) => REASON_DOT[reason] ?? SPARE_DOTS[index % SPARE_DOTS.length];

/**
  * HOW MANY EVIDENCE FILES EACH REASON NEEDS.
  *
  * MIRRORS `EVIDENCE_MIN_BY_REASON` in the backend's transfer.service.ts, WHICH IS THE GATE — this
  * copy exists only so the form can ask up front instead of bouncing a finished count. Change both
  * together; the server refuses anything short regardless of what this says.
  */
export const EVIDENCE_MIN: Record<string, number> = {
  THEFT: 2,
  DAMAGE: 2,
  EXPIRY: 1,
  PRODUCTION_LOSS: 1,
  PHYSICAL_COUNT: 0,
  SAMPLE: 0,
  OTHER: 0,
};
export const evidenceMinFor = (reason?: string | null) => EVIDENCE_MIN[String(reason ?? '')] ?? 0;

const REASON_META = [
  { value: 'PHYSICAL_COUNT', label: 'Physical count correction', hint: 'the shelf and the book disagree' },
  { value: 'DAMAGE', label: 'Damaged stock', hint: 'broken, spoiled or contaminated' },
  { value: 'EXPIRY', label: 'Expired stock write-off', hint: 'past its expiry date' },
  { value: 'THEFT', label: 'Shrinkage / theft', hint: 'missing, and not accounted for' },
  { value: 'SAMPLE', label: 'Free sample / marketing', hint: 'given away, not sold' },
  { value: 'PRODUCTION_LOSS', label: 'Production loss', hint: 'lost while packing or blending' },
  { value: 'OTHER', label: 'Other (specify in notes)', hint: 'say what happened in the note below' },
];

/** The reason codes an adjustment can carry. Exported so the register writes the SAME words. */
export const REASON_OPTIONS = REASON_META.map((r) => ({ value: r.value, label: r.label }));

/** Code -> the words a reader sees. An unknown code (older data, an import) prints as it is stored. */
export const reasonLabel = (code?: string | null) =>
  REASON_META.find((o) => o.value === code)?.label ?? (code ?? 'OTHER');

/** The dropdown's own rows: a coloured dot, the reason, and what it means — same hue as the register. */
const REASON_SELECT_OPTIONS = REASON_META.map((r) => ({
  value: r.value,
  searchLabel: `${r.label} ${r.hint}`,
  selectedText: (
    <span className="inline-flex min-w-0 items-center gap-2 align-middle">
      <span className={cn('size-2 shrink-0 rounded-pill', dotFor(r.value))} />
      <span className="truncate">{r.label}</span>
    </span>
  ),
  label: (
    <span className="flex min-w-0 items-start gap-2">
      <span className={cn('mt-1.5 size-2 shrink-0 rounded-pill', dotFor(r.value))} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-content">{r.label}</span>
        <span className="block truncate type-caption text-subtle">{r.hint}</span>
      </span>
      {/* The evidence bar is part of choosing the reason, not a surprise at the end. */}
      {EVIDENCE_MIN[r.value] > 0 && (
        <Tag tone={EVIDENCE_MIN[r.value] > 1 ? 'red' : 'gold'} style={{ margin: 0, flexShrink: 0 }}>
          {EVIDENCE_MIN[r.value]} proof{EVIDENCE_MIN[r.value] > 1 ? 's' : ''}
        </Tag>
      )}
    </span>
  ),
}));

/** One product-and-lot being corrected. A count carries several. */
type Line = {
  /** Stable identity for React + the error map — the product can change under it. */
  key: string;
  /** The base product, chosen first. Its variants are then a short list instead of one long one. */
  productName?: string;
  productId?: string;
  batchId?: string;
  qty?: number | null;
  direction: 'IN' | 'OUT';
};

let lineSeq = 0;
const blankLine = (direction: 'IN' | 'OUT' = 'OUT'): Line => ({ key: `line-${++lineSeq}`, direction });

type StockRow = { productId: string; sku?: string; name: string; variantLabel?: string; avail: number };

/**
 * ONE LINE OF A COUNT.
 *
 * It owns its own batch query rather than being handed one: two lines on the same product share a
 * single fetch (react-query dedupes by key), and a line whose product is still unpicked fetches
 * nothing at all. Lifting that into the parent would mean refetching every lot in the warehouse
 * each time any line changed.
 */
function LineCard({
  index, line, warehouseId, inStock, error, canRemove, onChange, onRemove,
}: {
  index: number;
  line: Line;
  warehouseId?: string;
  inStock: StockRow[];
  error?: string;
  canRemove: boolean;
  onChange: (next: Line) => void;
  onRemove: () => void;
}) {
  const { data: batches } = useQuery({
    queryKey: ['batches-for-adj', line.productId, warehouseId],
    queryFn: () => request<any[]>({ url: '/batches', params: { productId: line.productId, warehouseId } }),
    enabled: !!line.productId && !!warehouseId,
  });

  const selectedBatch = (batches ?? []).find((b: any) => String(b.id) === String(line.batchId));
  const batchAvail = selectedBatch != null ? Number(selectedBatch.availableQty ?? selectedBatch.qty ?? 0) : undefined;
  // An OUT against a NAMED lot can't exceed that lot. Left blank, the server takes the oldest lots
  // first and spans as many as it needs, so there is nothing to cap here.
  const outCap = line.direction === 'OUT' && batchAvail != null ? batchAvail : undefined;

  /*
   * PRODUCT FIRST, THEN ITS SIZE.
   *
   * The catalogue is one row per VARIANT, so a flat picker listed "AGN (0.5L)", "AGN (1L)",
   * "AGN LTE (0.25L)", "AGN LTE (0.5L)"… — forty near-identical rows to scroll, with the real
   * choice (which product?) buried inside the repetition. Choosing the product first turns the
   * second list into two or three obvious options.
   */
  const products = useMemo(() => {
    const byName = new Map<string, { name: string; avail: number; variants: StockRow[] }>();
    for (const p of inStock) {
      const g = byName.get(p.name) ?? { name: p.name, avail: 0, variants: [] };
      g.avail += p.avail;
      g.variants.push(p);
      byName.set(p.name, g);
    }
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [inStock]);

  const group = products.find((g) => g.name === line.productName);
  const variants = group?.variants ?? [];
  const variant = variants.find((v) => String(v.productId) === String(line.productId));
  const set = (patch: Partial<Line>) => onChange({ ...line, ...patch });

  const pickProduct = (name?: string) => {
    const g = products.find((x) => x.name === name);
    // One size only? There is nothing to choose — pick it, and the variant control says so.
    const only = g && g.variants.length === 1 ? g.variants[0] : undefined;
    set({ productName: name, productId: only ? String(only.productId) : undefined, batchId: undefined });
  };

  return (
    <div className={cn('rounded-card border bg-surface p-3', error ? 'border-danger-500/45' : 'border-line')}>
      <div className="mb-2 flex items-center gap-2">
        <span className="grid size-5 shrink-0 place-items-center rounded-pill bg-surface-3 type-caption-strong text-muted">
          {index + 1}
        </span>
        <span className="min-w-0 flex-1 truncate type-caption text-subtle">
          {variant
            ? `${variant.name}${variant.variantLabel ? ` · ${variant.variantLabel}` : ''}${variant.sku ? ` · ${variant.sku}` : ''}`
            : line.productName ?? 'Nothing picked yet'}
        </span>
        {variant && <span className="shrink-0 type-caption text-subtle">in stock {fmtQty(variant.avail)}</span>}
        {canRemove && (
          <Button
            size="sm" variant="soft-danger" className="shrink-0"
            aria-label={`Remove entry ${index + 1}`}
            icon={<Trash2 className="size-4" />}
            onClick={onRemove}
          />
        )}
      </div>

      {/*
        * Each field takes the width its CONTENT needs, not an equal slice of twelve.
        *
        * A rigid grid gave a three-digit quantity the same room as a lot number, so the row carried
        * dead space in the middle while the batch chip truncated. These grow in proportion to what
        * they hold — product and batch widest, size and quantity tight — and direction is fixed,
        * because a two-option toggle has one correct width and stretching it only clips it.
        */}
      <div className="flex flex-wrap items-start gap-2">
        <Field label="Product" required className="min-w-[168px] flex-[3_1_168px]">
          <Select
            showSearch
            optionFilterProp="searchLabel"
            placeholder={warehouseId ? 'Which product?' : 'Select a warehouse first'}
            disabled={!warehouseId}
            // The Select shows this both for an empty list and for a search that matched nothing, so only
            // an EMPTY list may say the warehouse holds no stock ("Neem" at WH-MUM said it did not).
            notFoundContent={!warehouseId ? 'Select a warehouse'
              : products.length ? 'No product in stock here matches' : 'No stock in this warehouse'}
            value={line.productName}
            onChange={(v: any) => pickProduct(v)}
            options={products.map((g) => ({
              value: g.name,
              searchLabel: g.name,
              label: (
                <span className="flex items-center justify-between gap-3">
                  <span className="min-w-0 flex-1 truncate">{g.name}</span>
                  <span className="shrink-0 type-caption text-subtle">
                    {g.variants.length > 1 ? `${g.variants.length} sizes` : fmtQty(g.avail)}
                  </span>
                </span>
              ),
            }))}
          />
        </Field>

        <Field
          label="Size" required className="min-w-[104px] flex-[1.2_1_104px]"
          hint={group && group.variants.length === 1 ? 'only size' : undefined}
        >
          <Select
            showSearch
            optionFilterProp="searchLabel"
            placeholder={line.productName ? 'Which size?' : 'Pick a product'}
            disabled={!line.productName || variants.length <= 1}
            value={line.productId}
            onChange={(v: any) => set({ productId: v, batchId: undefined })}
            options={variants.map((v) => ({
              value: String(v.productId),
              searchLabel: `${v.variantLabel ?? ''} ${v.sku ?? ''}`,
              selectedText: <span className="truncate">{v.variantLabel ?? 'Standard'}</span>,
              label: (
                <span className="flex items-center justify-between gap-3">
                  <span className="min-w-0 flex-1 truncate">{v.variantLabel ?? 'Standard'}</span>
                  <Tag tone="blue" style={{ margin: 0, flexShrink: 0 }}>{v.avail}</Tag>
                </span>
              ),
            }))}
          />
        </Field>

        <Field
          label="Batch"
          className="min-w-[160px] flex-[2.6_1_160px]"
          hint={!line.batchId && line.direction === 'OUT' ? 'Blank = oldest lots first' : undefined}
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="searchLabel"
            // The collapsed trigger shows the chip without the row's stock tag — the whole node was
            // getting clipped mid-value in the closed control.
            optionLabelProp="selectedText"
            placeholder={line.productId ? 'Any lot' : 'Pick a size'}
            disabled={!line.productId || !warehouseId}
            value={line.batchId}
            onChange={(v: any) => set({ batchId: v ?? undefined })}
            options={(batches ?? []).map((b: any) => ({
              value: b.id,
              searchLabel: b.batchNo,
              selectedText: (
                <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 align-middle">
                  <CodeChip>{b.batchNo}</CodeChip>
                </span>
              ),
              label: (
                <span className="flex items-center justify-between gap-3">
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                    <CodeChip className="shrink-0">{b.batchNo}</CodeChip>
                    {b.expDate ? <span className="type-caption text-muted">exp {formatIstDate(b.expDate)}</span> : null}
                  </span>
                  <Tag tone="blue" style={{ margin: 0, flexShrink: 0 }}>{b.availableQty ?? b.qty ?? 0}</Tag>
                </span>
              ),
            }))}
          />
        </Field>

        <Field
          label="Quantity" required className="min-w-[88px] flex-[0.9_1_88px]"
          hint={outCap != null ? `max ${fmtQty(outCap)}` : undefined}
        >
          <InputNumber
            min={0.001}
            max={outCap}
            step={1}
            style={{ width: '100%' }}
            value={line.qty ?? undefined}
            onChange={(v: any) => set({ qty: v == null || v === '' ? null : Number(v) })}
          />
        </Field>

        <Field label="Direction" required className="w-[152px] shrink-0 grow-0">
          {/* `size="small"` + `block`: at the middle size the two pills plus the strip's own padding
              overflowed a two-column cell and the OUT pill was cut off at the card's edge. */}
          <Segmented
            block
            size="small"
            value={line.direction}
            onChange={(v) => set({ direction: v as 'IN' | 'OUT' })}
            options={[{ value: 'IN', label: 'IN +' }, { value: 'OUT', label: 'OUT −' }]}
          />
        </Field>
      </div>

      {error && <p className="mt-1.5 type-caption text-danger-600 dark:text-danger-400">{error}</p>}
    </div>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  editing?: any; // when set, the modal edits this (SUBMITTED-only) adjustment instead of creating one
};

/**
 * RAISE A STOCK CORRECTION — one line, or a whole count's worth.
 *
 * A count covers a shelf, not a SKU. The form used to carry exactly one product-and-lot, so a
 * fifteen-product count meant opening it fifteen times; it now carries a list, and the things the
 * ACT shares — the warehouse, the reason, the evidence photo, the note — are asked once at the top.
 * Each line still becomes its own adjustment record (that is what the ledger, the approvals queue
 * and the register all understand), but the submission is all-or-nothing.
 *
 * EDIT reuses the same layout with the list pinned to one line: editing an existing record cannot
 * grow it into several, and the PATCH route is unchanged.
 */
export function AdjustmentFormModal({ open, onClose, editing }: Props) {
  const canSeeWarehouses = useCanSeeWarehouses();
  /* Nobody queues for their own review — the API posts an approver's adjustment immediately (see
     submitAdjustments). So this form must not promise a review step that will not happen: the
     button, the confirm and the banner all say what will ACTUALLY occur when it is pressed. */
  const canApprove = usePermission('STOCK.ADJUST.APPROVE');
  const qc = useQueryClient();

  const [warehouseId, setWarehouseId] = useState<string | undefined>();
  const [reason, setReason] = useState<string>('PHYSICAL_COUNT');
  const [evidenceUrls, setEvidenceUrls] = useState<string[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [lines, setLines] = useState<Line[]>([blankLine()]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [headerError, setHeaderError] = useState<string | undefined>();
  const [evidenceError, setEvidenceError] = useState<string | undefined>();

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    // Gated so a role without warehouse access doesn't fire a call it is refused — see useCanSeeWarehouses.
    enabled: canSeeWarehouses && open,
  });

  // Catalog with live per-warehouse stock — the product list is what is actually IN the chosen
  // warehouse, with the quantity there. To add brand-new stock that isn't tracked yet, use GRN.
  const { data: catalog } = useQuery({
    queryKey: ['catalog-with-stock-adj'],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock' }),
    enabled: open,
  });

  const whCode = (warehouses ?? []).find((w: any) => String(w.id) === String(warehouseId))?.code as string | undefined;
  const inStock: StockRow[] = useMemo(() => (catalog ?? [])
    .map((p: any) => ({
      productId: String(p.productId), sku: p.sku, name: p.name, variantLabel: p.variantLabel,
      avail: Number(p.stock?.warehouses?.[whCode ?? ''] ?? 0),
    }))
    .filter((p) => p.avail > 0), [catalog, whCode]);

  /* Seed on OPEN, not on every render: the modal is kept mounted, so re-seeding on `editing` alone
     would wipe what the user has typed the moment any parent state changed. The edited row's base
     product name is resolved from the catalogue once it has loaded. */
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setWarehouseId(editing?.warehouseId != null ? String(editing.warehouseId) : undefined);
      setReason(editing?.reason ?? 'PHYSICAL_COUNT');
      setEvidenceUrls(editing?.evidenceUrls?.length ? editing.evidenceUrls
        : editing?.evidenceUrl ? [editing.evidenceUrl] : []);
      setNotes(editing?.notes ?? '');
      setLines([editing ? {
        key: `edit-${editing.id}`,
        productId: editing.productId != null ? String(editing.productId) : undefined,
        batchId: editing.batchId != null ? String(editing.batchId) : undefined,
        qty: Number(editing.qty),
        direction: editing.direction,
      } : blankLine()]);
      setErrors({});
      setHeaderError(undefined);
      setEvidenceError(undefined);
    }
    wasOpen.current = open;
  }, [open, editing]);

  // Backfill the edited line's base product once the catalogue arrives (the row carries only an id).
  useEffect(() => {
    if (!open || !editing || !inStock.length) return;
    setLines((ls) => ls.map((l) => (l.productId && !l.productName
      ? { ...l, productName: inStock.find((p) => String(p.productId) === String(l.productId))?.name }
      : l)));
  }, [open, editing, inStock]);

  // Changing the WAREHOUSE invalidates every product and lot beneath it — they belong to the old one.
  const prevWh = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!open) return;
    if (prevWh.current !== undefined && prevWh.current !== warehouseId) {
      setLines((ls) => ls.map((l) => ({ ...l, productName: undefined, productId: undefined, batchId: undefined })));
      setErrors({});
    }
    prevWh.current = warehouseId;
  }, [warehouseId, open]);

  const setLine = (key: string, next: Line) => setLines((ls) => ls.map((l) => (l.key === key ? next : l)));
  const addLine = () => setLines((ls) => [...ls, blankLine(ls[ls.length - 1]?.direction ?? 'OUT')]);
  const removeLine = (key: string) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));

  // A running total, so a long count can be checked against the sheet it was copied from.
  const totals = useMemo(() => {
    const filled = lines.filter((l) => l.productId && Number(l.qty) > 0);
    return {
      lines: filled.length,
      in: filled.filter((l) => l.direction === 'IN').reduce((n, l) => n + Number(l.qty), 0),
      out: filled.filter((l) => l.direction === 'OUT').reduce((n, l) => n + Number(l.qty), 0),
    };
  }, [lines]);

  const evidenceNeeded = evidenceMinFor(reason);
  const evidenceShort = Math.max(0, evidenceNeeded - evidenceUrls.length);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!warehouseId) setHeaderError('Choose the warehouse being corrected'); else setHeaderError(undefined);
    setEvidenceError(evidenceShort
      ? `${reasonLabel(reason)} needs ${evidenceNeeded} attachment${evidenceNeeded > 1 ? 's' : ''} — ${evidenceShort} more to go`
      : undefined);
    const seen = new Map<string, number>();
    lines.forEach((l, i) => {
      if (!l.productName) { next[l.key] = 'Pick a product'; return; }
      if (!l.productId) { next[l.key] = 'Pick which size'; return; }
      if (!(Number(l.qty) > 0)) { next[l.key] = 'Enter a quantity'; return; }
      /* The same product-and-lot twice is a double entry, not two corrections — both would post and
         the second would silently double the first. */
      const key = `${l.productId}::${l.batchId ?? 'FIFO'}`;
      const first = seen.get(key);
      if (first != null) next[l.key] = `Same product and batch as entry ${first + 1} — put the whole correction in one entry`;
      else seen.set(key, i);
    });
    setErrors(next);
    return !!warehouseId && !evidenceShort && Object.keys(next).length === 0;
  };

  const submit = useConfirmedMutation(editing
    ? { title: 'Save changes to this adjustment?', content: 'It stays pending and moves no stock; the edit is recorded against your name.', okText: 'Yes, save' }
    : canApprove
      ? { title: totals.lines > 1 ? `Post ${totals.lines} stock adjustments?` : 'Post this stock adjustment?', content: 'Warehouse stock changes immediately and every entry is recorded against your name.', okText: 'Yes, post' }
      : { title: totals.lines > 1 ? `Submit ${totals.lines} stock adjustments?` : 'Submit this stock adjustment?', content: 'They move no stock until an approver decides them, and are recorded against your name.', okText: 'Yes, submit' },
  {
    mutationFn: () => {
      if (editing) {
        const l = lines[0];
        return request({
          url: `/stock-adjustments/${editing.id}`, method: 'PATCH',
          data: {
            warehouseId, productId: l.productId, batchId: l.batchId ?? null,
            qty: Number(l.qty), direction: l.direction, reason,
            evidenceUrls, evidenceUrl: evidenceUrls[0] ?? null, notes: notes || null,
          },
        });
      }
      return request({
        url: '/stock-adjustments', method: 'POST',
        data: {
          warehouseId, reason,
          evidenceUrls: evidenceUrls.length ? evidenceUrls : undefined,
          notes: notes || undefined,
          lines: lines.map((l) => ({
            productId: l.productId, batchId: l.batchId ?? null, qty: Number(l.qty), direction: l.direction,
          })),
        },
      });
    },
    onSuccess: (res: any) => {
      const n = Number(res?.count ?? 1);
      message.success(
        editing ? 'Adjustment updated'
          : canApprove ? `${n === 1 ? 'Adjustment' : `${n} adjustments`} posted — stock updated`
            : `${n === 1 ? 'Adjustment' : `${n} adjustments`} submitted for approval`,
      );
      qc.invalidateQueries({ queryKey: ['stock-adjustments'] });
      qc.invalidateQueries({ queryKey: ['stock-adjustments-summary'] });
      // An approver's lines moved real stock on the way in, so the available quantities this form
      // itself reads are now stale for the next count.
      qc.invalidateQueries({ queryKey: ['catalog-with-stock-adj'] });
      qc.invalidateQueries({ queryKey: ['batches-for-adj'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const multi = !editing;
  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={editing ? 'Edit Stock Adjustment' : 'New Stock Adjustment'}
      width={multi ? 1040 : 820}
      footer={
        <>
          {/* The count so far sits beside the button that will post it, not buried above the lines. */}
          {multi && totals.lines > 0 && (
            <span className="me-auto type-caption text-subtle">
              {totals.lines} {totals.lines === 1 ? 'entry' : 'entries'}
              {totals.in > 0 ? ` · IN ${fmtQty(totals.in)}` : ''}
              {totals.out > 0 ? ` · OUT ${fmtQty(totals.out)}` : ''}
            </span>
          )}
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary" loading={submit.isPending}
            onClick={() => { if (validate()) submit.mutate(undefined as any); }}
          >
            {editing ? 'Save changes'
              : canApprove ? `Post ${totals.lines > 1 ? `${totals.lines} adjustments` : 'adjustment'}`
                : `Submit ${totals.lines > 1 ? `${totals.lines} adjustments` : 'for approval'}`}
          </Button>
        </>
      }
    >
      <Alert
        type="warning"
        style={{ marginBottom: 12 }}
        title={editing
          ? 'This correction is still pending — saving changes it, but posts nothing.'
          : canApprove
            ? 'Every entry posts to ledger stock the moment you save — you are its approver.'
            : 'Adjustments change ledger stock and need approval before they post.'}
        description="Attach photo/document evidence for damage, expiry or theft entries — auditors will look for it."
      />

      {/* Shared by the whole count — asked once. */}
      <div className="grid grid-cols-12 gap-3">
        <Field label="Warehouse" required error={headerError} className="col-span-12 sm:col-span-6">
          <Select
            showSearch
            optionFilterProp="searchLabel"
            placeholder="Where is the stock?"
            value={warehouseId}
            onChange={(v: any) => setWarehouseId(v)}
            options={(warehouses ?? []).map((w: any) => ({
              value: String(w.id),
              ...warehouseOption(w), // code chip + site name + a gold MASTER capsule on the master warehouse
            }))}
          />
        </Field>
        <Field label="Reason" required className="col-span-12 sm:col-span-6"
          hint={multi ? 'Applies to every entry below' : undefined}>
          <Select
            showSearch
            optionFilterProp="searchLabel"
            optionLabelProp="selectedText"
            value={reason}
            onChange={(v: any) => setReason(v)}
            options={REASON_SELECT_OPTIONS}
          />
        </Field>
      </div>

      <div className="mt-4 mb-2 flex items-center gap-2">
        <span className="type-overline text-muted">{multi ? 'Products to adjust' : 'What is being adjusted'}</span>
        <span className="h-px flex-1 bg-line" />
        {multi && (
          <Button size="sm" variant="ghost" icon={<Plus className="size-4" />} onClick={addLine}>
            Add product
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {lines.map((l, i) => (
          <LineCard
            key={l.key}
            index={i}
            line={l}
            warehouseId={warehouseId}
            inStock={inStock}
            error={errors[l.key]}
            canRemove={multi && lines.length > 1}
            onChange={(next) => setLine(l.key, next)}
            onRemove={() => removeLine(l.key)}
          />
        ))}
      </div>

      {multi && (
        <Button
          size="sm" variant="default" className="mt-2 w-full"
          icon={<Plus className="size-4" />} onClick={addLine}
        >
          Add another product
        </Button>
      )}

      <div className="mt-4 grid grid-cols-12 gap-3">
        <Field
          label={<span className="inline-flex items-center gap-1.5">
            Evidence (photos / documents)
            {evidenceNeeded > 0 && (
              <Tag tone={evidenceShort ? 'red' : 'green'}>
                {evidenceUrls.length} of {evidenceNeeded} required
              </Tag>
            )}
          </span>}
          required={evidenceNeeded > 0}
          error={evidenceError}
          className="col-span-12 sm:col-span-6"
          hint={evidenceNeeded > 0
            ? `${reasonLabel(reason)} is a serious write-off — attach at least ${evidenceNeeded} file${evidenceNeeded > 1 ? 's' : ''} (the goods, the label, an incident report).`
            : 'Optional for a routine correction. Attach anything that helps whoever reads this later.'}
        >
          <MultiFileUploadField
            category="evidence" buttonLabel="Upload evidence" max={8}
            value={evidenceUrls} onChange={(urls) => { setEvidenceUrls(urls); setEvidenceError(undefined); }}
          />
        </Field>
        <Field label="Notes" className="col-span-12 sm:col-span-6"
          hint={multi ? 'Kept on every entry of this count' : undefined}>
          <Input.TextArea
            rows={3} value={notes} onChange={(e: any) => setNotes(e.target.value)}
            placeholder="Free text — what happened, why, who saw it."
          />
        </Field>
      </div>
    </Modal>
  );
}
